import { ChangeEvent, useEffect, useState } from "react";
import { FieldPath, FieldPathValue, FieldValues, UseFormSetValue } from "react-hook-form";
import useI18n from "../../hooks/useTranslations";
import { getUITranslationLanguages, UI_TRANSLATION_LANGUAGE_CODES, UITranslationLanguageCode } from "../../types";
import FormField from "./FormField";
import FormFieldsContainer from "./FormFieldsContainer";
import Input from "./Input";
import ModalDialog from "./ModalDialog";
import Textarea from "./Textarea";
import styles from "./TranslationsModalDialog.module.css";

type TranslationValue = string | null | undefined;

type TranslationFieldKey<
  TField extends string,
  TLanguage extends UITranslationLanguageCode = UITranslationLanguageCode,
> = `${TField}${Capitalize<TLanguage>}`;

type BaseTranslationFieldName<TForm extends FieldValues, TLanguage extends UITranslationLanguageCode> = {
  [TKey in Extract<keyof TForm, string>]: TKey extends `${infer TBase}${Capitalize<TLanguage>}`
    ? TForm[TKey] extends TranslationValue
      ? TBase
      : never
    : never;
}[Extract<keyof TForm, string>];

type CommonTranslationFieldName<
  TForm extends FieldValues,
  TLanguages extends readonly UITranslationLanguageCode[] = typeof UI_TRANSLATION_LANGUAGE_CODES,
> = TLanguages extends readonly [
  infer TLanguage extends UITranslationLanguageCode,
  ...infer TRest extends readonly UITranslationLanguageCode[],
]
  ? Extract<BaseTranslationFieldName<TForm, TLanguage>, CommonTranslationFieldName<TForm, TRest>>
  : Extract<keyof TForm, string>;

type TranslationFieldName<TForm extends FieldValues> = Extract<
  CommonTranslationFieldName<TForm>,
  Extract<keyof TForm, string>
>;

type TranslationFieldPath<
  TForm extends FieldValues,
  TField extends TranslationFieldName<TForm>,
  TLanguage extends UITranslationLanguageCode = UITranslationLanguageCode,
> = Extract<TranslationFieldKey<TField, TLanguage>, FieldPath<TForm>>;

type DraftTranslationsProps<TForm extends FieldValues, TField extends TranslationFieldName<TForm>> = {
  isOpen: boolean;
  onClose: () => void;
  field: TField;
  values: TForm;
  setValue: UseFormSetValue<TForm>;
  title?: string;
} & (
  | {
      type?: "input";
      rows?: never;
    }
  | {
      type: "textarea";
      rows?: number;
    }
);

type DraftTranslations = Record<UITranslationLanguageCode, string>;

export type TranslationDialogFieldName<TForm extends FieldValues> = TranslationFieldName<TForm>;

const getTranslationFieldKey = <TField extends string, TLanguage extends UITranslationLanguageCode>(
  field: TField,
  language: TLanguage,
): TranslationFieldKey<TField, TLanguage> =>
  `${field}${language.charAt(0).toUpperCase()}${language.slice(1)}` as TranslationFieldKey<TField, TLanguage>;

const getTranslationValue = <TForm extends FieldValues, TField extends TranslationFieldName<TForm>>(
  values: TForm,
  field: TField,
  language: UITranslationLanguageCode,
) => {
  const translationField = getTranslationFieldKey(field, language) as TranslationFieldPath<
    TForm,
    TField,
    typeof language
  >;
  return (values[translationField] as TranslationValue) ?? "";
};

const buildDraft = <TForm extends FieldValues, TField extends TranslationFieldName<TForm>>(
  values: TForm,
  field: TField,
): DraftTranslations =>
  UI_TRANSLATION_LANGUAGE_CODES.reduce((acc, language) => {
    acc[language] = getTranslationValue(values, field, language);
    return acc;
  }, {} as DraftTranslations);

const TranslationsModalDialog = <TForm extends FieldValues, TField extends TranslationFieldName<TForm>>({
  isOpen,
  onClose,
  field,
  values,
  setValue,
  title,
  type = "input",
  rows = 5,
}: DraftTranslationsProps<TForm, TField>) => {
  const i18n = useI18n();
  const languageLabels = getUITranslationLanguages(i18n.translation).reduce(
    (acc, language) => {
      acc[language.value] = language.display;
      return acc;
    },
    {} as Record<UITranslationLanguageCode, string>,
  );
  const [draft, setDraft] = useState<DraftTranslations>(() => buildDraft(values, field));

  useEffect(() => {
    if (isOpen) {
      setDraft(buildDraft(values, field));
    }
  }, [field, isOpen, values]);

  if (!isOpen) {
    return null;
  }

  const handleSave = () => {
    UI_TRANSLATION_LANGUAGE_CODES.forEach((language) => {
      const translationField = getTranslationFieldKey(field, language) as TranslationFieldPath<
        TForm,
        TField,
        typeof language
      >;
      setValue(translationField, draft[language] as FieldPathValue<TForm, typeof translationField>, {
        shouldDirty: true,
      });
    });
    onClose();
  };

  const updateDraft = (language: UITranslationLanguageCode, nextValue: string) => {
    setDraft((prevState) => ({
      ...prevState,
      [language]: nextValue,
    }));
  };

  return (
    <ModalDialog
      onClose={onClose}
      title={title ?? i18n.translation.common.translations}
      className={type === "textarea" ? styles.textareaDialog : undefined}
      actions={[
        {
          title: i18n.translation.common.cancel,
          onClick: onClose,
          type: "secondary",
        },
        {
          title: i18n.translation.common.ok,
          onClick: handleSave,
        },
      ]}
      isSubmitting={false}
    >
      {type === "textarea" ? (
        <div className={styles.textareaGrid}>
          {UI_TRANSLATION_LANGUAGE_CODES.map((language) => (
            <FormField key={language} label={languageLabels[language]} isOptional className={styles.textareaField}>
              <Textarea
                rows={rows}
                value={draft[language]}
                onChange={(e: ChangeEvent<HTMLTextAreaElement>) => updateDraft(language, e.target.value)}
              />
            </FormField>
          ))}
        </div>
      ) : (
        <FormFieldsContainer>
          {UI_TRANSLATION_LANGUAGE_CODES.map((language) => (
            <FormField key={language} label={languageLabels[language]} isOptional>
              <Input
                value={draft[language]}
                onChange={(e: ChangeEvent<HTMLInputElement>) => updateDraft(language, e.target.value)}
              />
            </FormField>
          ))}
        </FormFieldsContainer>
      )}
    </ModalDialog>
  );
};

export default TranslationsModalDialog;
